import './StatsTab.css';
import GameStats from './GameStats.js';
import { DIFFICULTIES } from './difficulty.js';
import { useState, useRef, useEffect } from 'react';

const DAY_MS = 24 * 60 * 60 * 1000;
const WINDOW_DAYS = 30;

// Long enough that a stray tap while scrolling does not flash the percentages
const HOLD_MS = 200;

// Order matches the pie's sweep, so the legend reads around the chart
const SEGMENTS = [
  { key: 'clear', label: 'Cleared' },
  { key: 'complete', label: 'Complete' },
  { key: 'unsolved', label: 'Incomplete' },
];

/**
 * Board dimensions straight off the encoded string. Decoding every board in
 * history just to measure it would be wasted work.
 * @param {string} gameCode
 * @returns {{rows: number, cols: number}}
 */
function sizeOf(gameCode) {
  const rows = gameCode.split(';');
  return { rows: rows.length, cols: rows[0].split(',').length };
}

/**
 * Whole percentages that add up to 100. Rounding each share on its own leaves
 * 99 or 101 on screen often enough to notice, so the leftover points go to the
 * largest fractions.
 * @param {number[]} values
 * @param {number} total
 * @returns {number[]}
 */
function percentages(values, total) {
  if (total === 0) return values.map(() => 0);

  const exact = values.map((value) => (value / total) * 100);
  const result = exact.map(Math.floor);
  const leftover = 100 - result.reduce((sum, value) => sum + value, 0);

  exact
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort((a, b) => b.fraction - a.fraction)
    .slice(0, leftover)
    .forEach(({ index }) => { result[index] += 1; });

  return result;
}

/**
 * Boards per day across the trailing window, split by outcome. A board lands on
 * the first day it was touched inside the window and counts once, so a running
 * total reads as boards played rather than sessions.
 * The window is trimmed back to the first day with anything in it, so someone
 * who started three days ago sees three days rather than a long flat line
 * across an empty month.
 *
 * @param {Object[]} entries - from GameStats.getHistoryEntries()
 * @param {number} window - furthest back to look
 * @returns {{series: Object, days: number}} - oldest day first
 */
function dailyByStatus(entries, window) {
  const days = window;
  const midnight = new Date();
  midnight.setHours(0, 0, 0, 0);
  const startMs = midnight.getTime() - (days - 1) * DAY_MS;

  const series = {
    clear: new Array(days).fill(0),
    complete: new Array(days).fill(0),
    unsolved: new Array(days).fill(0),
  };

  entries.forEach(({ status, attempts = [], completes = [], clears = [] }) => {
    let first = null;
    [...attempts, ...completes, ...clears].forEach((timestamp) => {
      const dayStart = new Date(timestamp).setHours(0, 0, 0, 0);
      // Rounded, not floored - a DST shift makes one gap 23 or 25 hours
      const index = Math.round((dayStart - startMs) / DAY_MS);
      if (index >= 0 && index < days && (first === null || index < first)) first = index;
    });
    if (first !== null) series[status][first] += 1;
  });

  // Keep one empty day in front so the curve still rises from nothing
  const totals = Array.from({ length: days }, (_, i) =>
    series.clear[i] + series.complete[i] + series.unsolved[i]);
  const firstActive = totals.findIndex((count) => count > 0);
  const from = firstActive > 0 ? firstActive - 1 : 0;
  if (from === 0) return { series, days };

  return {
    series: {
      clear: series.clear.slice(from),
      complete: series.complete.slice(from),
      unsolved: series.unsolved.slice(from),
    },
    days: days - from,
  };
}

/** Running total, so the curve only ever climbs */
function accumulate(counts) {
  let carried = 0;
  return counts.map((value) => (carried += value));
}

const PieChart = ({ clear, complete, unsolved }) => {
  const counts = { clear, complete, unsolved };
  const total = clear + complete + unsolved;

  // Hold anywhere on the card to read the split as percentages, release to go
  // back. A peek rather than a toggle, so there is no state to get stuck in.
  const [asPercent, setAsPercent] = useState(false);
  const holdTimer = useRef(null);

  const startHold = () => {
    clearTimeout(holdTimer.current);
    holdTimer.current = setTimeout(() => setAsPercent(true), HOLD_MS);
  };
  const endHold = () => {
    clearTimeout(holdTimer.current);
    setAsPercent(false);
  };
  useEffect(() => () => clearTimeout(holdTimer.current), []);

  const shares = percentages(SEGMENTS.map(({ key }) => counts[key]), total);

  const clearPercent = total > 0 ? (clear / total) * 100 : 0;
  const completePercent = total > 0 ? (complete / total) * 100 : 0;

  const completeEnd = completePercent;
  const clearEnd = completeEnd + clearPercent;

  const gradient = `conic-gradient(
    var(--status-complete) 0% ${completeEnd}%,
    var(--status-clear) ${completeEnd}% ${clearEnd}%,
    var(--status-unsolved) ${clearEnd}% 100%
  )`;

  return (
    <div
      className="pie-chart-container"
      onPointerDown={startHold}
      onPointerUp={endHold}
      onPointerLeave={endHold}
      onPointerCancel={endHold}
    >
      <div className="pie-chart-wrapper">
        <div className="pie-chart" style={{ background: gradient }}>
          <div className="pie-chart-inner">
            <span className="pie-chart-total">{total}</span>
            <span className="pie-chart-label">played</span>
          </div>
        </div>
      </div>

      {/* Stacked columns rather than a row of "dot label value" triples, which
          ran past the edge of a 375px phone */}
      <div className="pie-chart-legend">
        {SEGMENTS.map(({ key, label }, index) => (
          // The hold is invisible to a screen reader, so the label carries both
          <div
            className="legend-item"
            key={key}
            aria-label={`${label}: ${counts[key]}, ${shares[index]} percent`}
          >
            <span className="legend-head">
              <span className={`legend-dot ${key}`}></span>
              <span className="legend-value">
                {asPercent ? `${shares[index]}%` : counts[key]}
              </span>
            </span>
            <span className="legend-label">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const ActivityChart = ({ series, days }) => {
  // Stacked bottom-up in legend order, so each band's top edge is the running
  // total of everything at or below it and the topmost edge is every board
  let below = new Array(days).fill(0);
  const bands = SEGMENTS.map(({ key, label }) => {
    const lower = below;
    const upper = accumulate(series[key]).map((value, index) => value + lower[index]);
    below = upper;
    return { key, label, upper, lower };
  });

  const last = days - 1;
  const total = below[last];

  // Fixed viewBox, fluid width - the SVG scales to whatever the phone gives it,
  // and non-scaling-stroke keeps every edge one pixel at any size
  const WIDTH = 320;
  const BASELINE = 78;
  const HEIGHT = 84;

  const x = (index) => (index / last) * WIDTH;
  const y = (value) => BASELINE - (value / total) * (BASELINE - 8);
  const point = (value, index) => `${x(index).toFixed(1)} ${y(value).toFixed(1)}`;

  const edge = (values) => values.map((v, i) => `${i === 0 ? 'M' : 'L'}${point(v, i)}`).join(' ');
  // Out along the top edge, back along the one below it
  const bandPath = (upper, lower) =>
    `${edge(upper)} ${lower.map((v, i) => `L${point(v, i)}`).reverse().join(' ')} Z`;

  return (
    <div className="activity-card">
      <div className="activity-head">
        <h3 className="activity-title">Last {days} days</h3>
        <span className="activity-total">{total} played</span>
      </div>

      {total === 0 ? (
        <p className="activity-empty">Nothing played in this window.</p>
      ) : (
        <>
          <svg
            className="activity-chart"
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            role="img"
            aria-label={
              `${total} board${total === 1 ? '' : 's'} played over the last ${days} days: ` +
              bands.map(({ label, upper, lower }) =>
                `${upper[last] - lower[last]} ${label.toLowerCase()}`).join(', ')
            }
          >
            <line className="activity-axis" x1="0" y1={BASELINE} x2={WIDTH} y2={BASELINE} vectorEffect="non-scaling-stroke" />
            {bands.map(({ key, upper, lower }) => (
              <path key={key} className={`activity-band ${key}`} d={bandPath(upper, lower)} />
            ))}
            {bands.map(({ key, upper }) => (
              <path key={key} className={`activity-edge ${key}`} d={edge(upper)} vectorEffect="non-scaling-stroke" />
            ))}
          </svg>
          {/* The axis starts where the data does, so it says so */}
          <div className="activity-axis-labels">
            <span>{days - 1}d ago</span>
            <span>today</span>
          </div>
        </>
      )}
    </div>
  );
};

function StatsTab() {
  const [mode, setMode] = useState('all');
  const handleModeChange = (e) => setMode(e.target.value);

  const target = DIFFICULTIES.find(({ name }) => name === mode);
  const entries = GameStats.getHistoryEntries();
  const filtered = target
    ? entries.filter(({ gameCode }) => {
        const { rows, cols } = sizeOf(gameCode);
        return rows === target.rows && cols === target.cols;
      })
    : entries;

  // getHistoryEntries already buckets each board into exactly one status
  const counts = { clear: 0, complete: 0, unsolved: 0 };
  filtered.forEach(({ status }) => { counts[status] += 1; });

  return (
    <>
      <div className="difficulty-selector invisible-hud">
        <select value={mode} onChange={handleModeChange} aria-label="Filter by board size">
          <option value="all">🗃️ All sizes</option>
          {DIFFICULTIES.map(({ name, label }) => (
            <option key={name} value={name}>{label}</option>
          ))}
        </select>
      </div>

      <section className="title-section">
        <h1>Statistics</h1>
      </section>

      {/* The body does not scroll, so this pane carries its own scroller - the
          same arrangement HistoryTab uses for its list */}
      <div className="stats-scroll">
        <PieChart
          clear={counts.clear}
          complete={counts.complete}
          unsolved={counts.unsolved}
        />
        <ActivityChart {...dailyByStatus(filtered, WINDOW_DAYS)} />
      </div>
    </>
  );
}

export default StatsTab;
